import type {
  ConfigDiffEntry,
  ConfigParameters,
  JsonSchema,
  UiSchemaElement,
} from './configTypes';

export const formatConfigValue = (value: unknown): string => {
  if (value === undefined || value === null) {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

// Server may send values as strings as that is menders expected format
const valuesEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a === undefined || a === null || b === undefined || b === null) {
    return false;
  }
  return String(a) === String(b);
};

export const computeConfigDiff = (
  current: ConfigParameters,
  next: ConfigParameters
): ConfigDiffEntry[] => {
  const diff: ConfigDiffEntry[] = [];

  for (const field of Object.keys(next)) {
    if (!valuesEqual(current[field], next[field])) {
      diff.push({ field, current: current[field], next: next[field] });
    }
  }

  return diff.sort((a, b) => a.field.localeCompare(b.field));
};

// Minimal shape of an AJV validation error (as surfaced by JSONForms onChange).
export interface ValidationErrorLike {
  instancePath?: string;
  message?: string;
  params?: { additionalProperty?: string };
}

// Resolve the field name an AJV error refers to. Field-level errors carry an
// instancePath like "/site_latitude"; root-level errors (e.g. additionalProperties)
// instead name the offending key in params.additionalProperty.
export const fieldFromValidationError = (error: ValidationErrorLike): string | null => {
  if (error.instancePath) {
    return error.instancePath.replace(/^\//, '');
  }
  return error.params?.additionalProperty ?? null;
};

// Filter validation errors down to the ones that should block a deploy: only
// errors on a field we are actually setting, as a device can have fields that
// the current schema would not accept as valid.
export const blockingValidationErrors = (
  errors: ValidationErrorLike[],
  setFields: string[]
): ValidationErrorLike[] => {
  const sent = new Set(setFields);
  return errors.filter((error) => {
    const field = fieldFromValidationError(error);
    return field !== null && sent.has(field);
  });
};

// Map of the schema's declared default values, keyed by property name. Used to
// pre-fill the form so every field shows a concrete value — the device's reported
// value where it has one, the schema default otherwise — instead of a blank '—'.
export const schemaDefaults = (schema: JsonSchema | null): ConfigParameters => {
  const properties = (schema?.properties ?? {}) as Record<string, { default?: unknown }>;
  const result: ConfigParameters = {};

  for (const [key, prop] of Object.entries(properties)) {
    if (prop && 'default' in prop) {
      result[key] = prop.default;
    }
  }

  return result;
};

const SITE_GROUP = 'site';
const OTHER_GROUP = 'other';
// Per-plugin scheduling knobs, pinned to the bottom of each group in this order.
const SCHEDULE_FIELDS = ['duration_minutes', 'interval_minutes', 'enabled'];

const titleCase = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1);

// Build schema out by plugin key prefixes. ie. site_*, sealion_*, vision_+
// ranked to maintain an order of displayed fields. Site first, intervals last per plugin
export const buildUiSchema = (schema: JsonSchema | null): UiSchemaElement | undefined => {
  const keys = Object.keys((schema?.properties ?? {}) as Record<string, unknown>);
  if (keys.length === 0) {
    return undefined;
  }

  const groups: Record<string, string[]> = {};
  for (const key of keys) {
    const separator = key.indexOf('_');
    const group = separator > 0 ? key.slice(0, separator) : OTHER_GROUP;
    groups[group] = [...(groups[group] ?? []), key];
  }

  const rank = (group: string): number =>
    group === SITE_GROUP ? 0 : group === OTHER_GROUP ? 2 : 1;

  // 0 for regular fields, 1+ for scheduling fields. Stable, so schema order is otherwise kept.
  const scheduleRank = (key: string): number =>
    SCHEDULE_FIELDS.indexOf(key.slice(key.indexOf('_') + 1)) + 1;

  const elements = Object.keys(groups)
    .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
    .map((group) => ({
      type: 'Group',
      label: titleCase(group),
      elements: groups[group]
        .map((key, index) => ({ key, index }))
        .sort((a, b) => scheduleRank(a.key) - scheduleRank(b.key) || a.index - b.index)
        .map(({ key }) => ({
          type: 'Control',
          scope: `#/properties/${key}`,
        })),
    }));

  return { type: 'VerticalLayout', elements };
};

type OutcomeColor = 'success' | 'error' | 'warning' | 'info' | 'default';

export interface DeploymentOutcome {
  label: string;
  color: OutcomeColor;
}

const OUTCOME_BY_STAT: Record<string, DeploymentOutcome> = {
  success: { label: 'Success', color: 'success' },
  'already-installed': { label: 'Already installed', color: 'success' },
  failure: { label: 'Failed', color: 'error' },
  aborted: { label: 'Aborted', color: 'error' },
  noartifact: { label: 'No artifact', color: 'error' },
  artifact_too_big: { label: 'Artifact too big', color: 'error' },
  decommissioned: { label: 'Decommissioned', color: 'error' },
  downloading: { label: 'Downloading', color: 'info' },
  installing: { label: 'Installing', color: 'info' },
  rebooting: { label: 'Rebooting', color: 'info' },
  pending: { label: 'Pending', color: 'warning' },
  pause_before_installing: { label: 'Paused', color: 'warning' },
  pause_before_rebooting: { label: 'Paused', color: 'warning' },
  pause_before_committing: { label: 'Paused', color: 'warning' },
};

// Fallback when no per-device statistic is recorded yet, keyed by the
// deployment-level status.
const OUTCOME_BY_STATUS: Record<string, DeploymentOutcome> = {
  finished: { label: 'Finished', color: 'default' },
  inprogress: { label: 'In progress', color: 'info' },
  pending: { label: 'Pending', color: 'warning' },
};

export const deploymentOutcome = (deployment: {
  status?: string;
  statistics?: { status?: Record<string, number> };
}): DeploymentOutcome => {
  const counts = deployment.statistics?.status ?? {};
  const nonZero = Object.keys(counts).filter((key) => (counts[key] ?? 0) > 0);

  if (nonZero.length > 0) {
    const key = nonZero.includes('failure') ? 'failure' : nonZero[0];
    return OUTCOME_BY_STAT[key] ?? { label: key, color: 'default' };
  }

  return (
    (deployment.status ? OUTCOME_BY_STATUS[deployment.status] : undefined) ?? {
      label: deployment.status ?? 'unknown',
      color: 'default',
    }
  );
};

// Format the elapsed time between a deployment's creation and completion as a
// compact "20s" / "2m 24s". Returns null when it can't be computed.
export const formatDeploymentDuration = (
  created?: string,
  finished?: string
): string | null => {
  if (!created || !finished) {
    return null;
  }
  const ms = Date.parse(finished) - Date.parse(created);
  if (!Number.isFinite(ms) || ms < 0) {
    return null;
  }
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  return `${Math.floor(totalSeconds / 60)}m ${totalSeconds % 60}s`;
};

// Diff entries for fields an overwrite will drop: keys present on the device but
// not defined by the schema. Marked `removed` so the confirmation diff can show
// the user exactly what is being discarded rather than dropping it silently.
export const droppedFieldEntries = (
  current: ConfigParameters,
  schema: JsonSchema | null
): ConfigDiffEntry[] => {
  const properties = (schema?.properties ?? {}) as Record<string, unknown>;

  return Object.keys(current)
    .filter((key) => !(key in properties))
    .map((field) => ({ field, current: current[field], next: undefined, removed: true }))
    .sort((a, b) => a.field.localeCompare(b.field));
};

// Overwrite sends ONLY the changed fields; every omitted field is removed on the device,
// which resets it to its default. The diff still surfaces those resets (and dropped non-schema fields)
// so the human check shows the full end state.
export const buildOverwrite = (
  baseline: ConfigParameters,
  changeDiff: ConfigDiffEntry[],
  schema: JsonSchema | null
): { payload: ConfigParameters; diff: ConfigDiffEntry[] } => {
  // Payload is just the explicit edits — absent keys reset to default on the device.
  const payload = Object.fromEntries(changeDiff.map((d) => [d.field, d.next]));

  const changedFields = new Set(changeDiff.map((d) => d.field));
  const defaults = schemaDefaults(schema);
  const properties = (schema?.properties ?? {}) as Record<string, unknown>;

  // Edits the user made.
  const edits = changeDiff.map((entry) => ({ ...entry, reset: false }));

  // Unchanged schema fields whose device value differs from the default: the overwrite drops them, resetting to default.
  const resets = Object.keys(properties)
    .filter((field) => !changedFields.has(field) && field in baseline)
    .filter((field) => !valuesEqual(baseline[field], defaults[field]))
    .map((field) => ({ field, current: baseline[field], next: defaults[field], reset: true }));

  const diff = [...edits, ...resets].sort((a, b) => a.field.localeCompare(b.field));
  return { payload, diff: [...diff, ...droppedFieldEntries(baseline, schema)] };
};
